import React from 'react';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { getStyle, SERVICE_CHAIN } from '../../../utils/utils';
import CBMediaQuery from '../../shared/CBMediaQuery';
import CBSubHeading from '../../shared/CBSubHeading';
import CBSpinnerFloating from '../../shared/CBSpinnerFloating';
import {
  fetchOrganizationVehicleSettings as _fetchOrganizationVehicleSettings,
  fetchOrganizationVehicleSettingsChoices as _fetchOrganizationVehicleSettingsChoices,
  updateOrganizationVehicleSettings as _updateOrganizationVehicleSettings,
} from '../../../actions/organizationVehicleSettingsActions';
import { fetchBusinessUnits as _fetchBusinessUnits } from '../../../actions/businessUnitActions';
import { fetchAssetCategories as _fetchAssetCategories } from '../../../actions/assetCategoryActions';
import OrganizationVehicleSettingsRow from './OrganizationVehicleSettingsRow';

const SHOW_MORE_STEP = 300;

// Table of an organization's vehicles with per-row autotalli, ajoneuvovastaava,
// vastuukorjaamo, toimipiste and ajoneuvo-/laitetyyppi — ajoneuvovastaava and
// vastuukorjaamo only for a fleet some workshop chain serves, and autotalli is
// editable only by the organization itself (the chain sees it read-only). Changes save
// immediately. Used by the fleet portal (organization from the settings
// selector) and by the chain portal (`organizationId` prop for the customer
// being edited).
class SettingsOrganizationVehicleSettings extends React.Component {
  state = {
    filter: '',
    visibleCount: SHOW_MORE_STEP,
  };

  componentDidMount() {
    this.fetchAll();
  }

  componentDidUpdate(prevProps) {
    if (this.getOrganizationId(prevProps) !== this.getOrganizationId()) {
      this.fetchAll();
    }
  }

  getOrganizationId = (props) => {
    const p = props || this.props;
    return p.organizationId || p.selectedOrganizationId;
  };

  getService = () => this.props.service || this.props.appService;

  fetchAll = () => {
    const organizationId = this.getOrganizationId();
    if (!organizationId || organizationId === 'all') return;
    const {
      fetchOrganizationVehicleSettings, fetchOrganizationVehicleSettingsChoices, fetchBusinessUnits,
      fetchAssetCategories,
    } = this.props;
    fetchOrganizationVehicleSettings(organizationId);
    fetchOrganizationVehicleSettingsChoices(organizationId);
    fetchBusinessUnits(organizationId);
    fetchAssetCategories(organizationId);
  };

  getUserRole = () => {
    const { user } = this.props;
    const organizationId = this.getOrganizationId();
    if (!user || !user.organizations || !organizationId) return null;
    const organization = user.organizations.find((org) => org.id == organizationId);
    return organization ? organization.user_role : null;
  };

  // Ajoneuvovastaava and vastuukorjaamo are chain care service concepts: a fleet
  // no chain serves only picks toimipiste. The choices payload is the authority
  // (it knows about active chain care services); until it lands, the user's own
  // organization row answers, so the columns do not flicker for a chain fleet.
  belongsToChain = () => {
    const { choices, user } = this.props;
    const organizationId = this.getOrganizationId();
    if (choices && choices.organization_id == organizationId) {
      return !!choices.has_chain_care_service;
    }
    // The chain portal only lists the chain's own care service customers.
    if (this.getService() === SERVICE_CHAIN) return true;
    const organizations = (user && user.organizations) || [];
    const organization = organizations.find((org) => org.id == organizationId);
    return !!(organization && organization.workshop_chain_ids && organization.workshop_chain_ids.length);
  };

  canEdit = () => {
    const { user } = this.props;
    if (user && user.is_staff) return true;
    if (this.getService() === SERVICE_CHAIN) return true;
    const role = this.getUserRole();
    return role === 'admin' || role === 'manager';
  };

  // The choices payload says whether this user may move vehicles between the
  // organization's garages (never the serving chain).
  canChangeGarage = () => {
    const { choices } = this.props;
    const organizationId = this.getOrganizationId();
    return !!(choices && choices.organization_id == organizationId && choices.can_change_garage);
  };

  onFieldChange = (vehicleId, field, value) => {
    const { updateOrganizationVehicleSettings } = this.props;
    const data = {};
    data[field] = value;
    updateOrganizationVehicleSettings(vehicleId, data, this.getOrganizationId());
  };

  // Built once per render — never per row.
  getOptions = () => {
    const {
      t, choices, businessUnits, assetCategories,
    } = this.props;
    const vehicleManagerOptions = [{ label: t('noVehicleManager'), value: '' }];
    if (choices && choices.vehicle_managers) {
      choices.vehicle_managers.forEach((u) => {
        vehicleManagerOptions.push({ label: u.name, value: String(u.id) });
      });
    }
    const workshopOptions = [{ label: t('csDefaultWorkshop', { ns: 'ChainSettingsView' }), value: '' }];
    if (choices && choices.workshops) {
      choices.workshops.forEach((w) => {
        workshopOptions.push({ label: w.name, value: String(w.id) });
      });
    }
    // The toimipiste list comes from the business-unit store so edits on the
    // Toimipisteet page (or card above, in the chain portal) show up at once.
    const units = businessUnits && businessUnits.length
      ? businessUnits
      : ((choices && choices.business_units) || []);
    const businessUnitOptions = [{ label: t('noBusinessUnit'), value: '' }];
    units.forEach((unit) => {
      businessUnitOptions.push({ label: unit.name, value: String(unit.id) });
    });
    const categories = assetCategories && assetCategories.length
      ? assetCategories
      : ((choices && choices.asset_categories) || []);
    const assetCategoryOptions = [{ label: t('noAssetCategory'), value: '' }];
    categories.forEach((category) => {
      assetCategoryOptions.push({ label: category.name, value: String(category.id) });
    });
    // A vehicle always has a garage: no empty option.
    const garageOptions = ((choices && choices.garages) || []).map((garage) => (
      { label: garage.name, value: String(garage.id) }
    ));
    return {
      vehicleManagerOptions, workshopOptions, businessUnitOptions, assetCategoryOptions, garageOptions,
    };
  };

  getFilteredRows = () => {
    const { rows } = this.props;
    const query = this.state.filter.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => {
      const haystack = [row.registration_number, row.manufacturer, row.model_name]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.indexOf(query) !== -1;
    });
  };

  render() {
    const {
      t, rows, rowStatus, status, embedded,
    } = this.props;
    const { filter, visibleCount } = this.state;
    const organizationId = this.getOrganizationId();
    if (!organizationId || organizationId === 'all') {
      return null;
    }
    const canEdit = this.canEdit();
    const canChangeGarage = canEdit && this.canChangeGarage();
    const showChainFields = this.belongsToChain();
    const options = this.getOptions();
    const filteredRows = this.getFilteredRows();
    const visibleRows = filteredRows.slice(0, visibleCount);
    const noCandidates = showChainFields && options.vehicleManagerOptions.length === 1;

    return (
      <CBMediaQuery>
        {screenSize => (
          <div style={getStyle(screenSize, styles, embedded ? 'embeddedContainer' : 'container')}>
            {status && status.loading && <CBSpinnerFloating />}
            <div style={getStyle(screenSize, styles, 'sectionContainer')}>
              <CBSubHeading>{t('heading')}</CBSubHeading>
            </div>
            <div style={getStyle(screenSize, styles, 'sectionContainer')}>
              <p>{showChainFields ? t('description') : t('descriptionBusinessUnitOnly')}</p>
              {noCandidates && (
                <p style={getStyle(screenSize, styles, 'hint')}>{t('noVehicleManagerCandidates')}</p>
              )}
            </div>
            <div style={getStyle(screenSize, styles, 'toolbar')}>
              <input
                type='text'
                value={filter}
                onChange={(e) => this.setState({ filter: e.target.value, visibleCount: SHOW_MORE_STEP })}
                placeholder={t('filterPlaceholder')}
                className='white-placeholder-input'
                style={getStyle(screenSize, styles, 'searchInput')}
              />
              <span style={getStyle(screenSize, styles, 'count')}>
                {t('vehicleCount', { count: filteredRows.length })}
              </span>
            </div>
            {!rows.length && !(status && status.loading) && (
              <p style={getStyle(screenSize, styles, 'hint')}>{t('noVehicles')}</p>
            )}
            {screenSize === 'small' ? (
              <div>
                {visibleRows.map((row) => (
                  <OrganizationVehicleSettingsRow
                    key={row.vehicle_id}
                    row={row}
                    screenSize={screenSize}
                    rowStatus={rowStatus[row.vehicle_id]}
                    canEdit={canEdit}
                    canChangeGarage={canChangeGarage}
                    garageOptions={options.garageOptions}
                    showChainFields={showChainFields}
                    vehicleManagerOptions={options.vehicleManagerOptions}
                    workshopOptions={options.workshopOptions}
                    businessUnitOptions={options.businessUnitOptions}
                    assetCategoryOptions={options.assetCategoryOptions}
                    onFieldChange={this.onFieldChange}
                  />
                ))}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th style={getStyle(screenSize, styles, 'tableHeading')}>{t('headingVehicle')}</th>
                      <th style={getStyle(screenSize, styles, 'tableHeading')}>{t('headingGarage')}</th>
                      {showChainFields && (
                        <th style={getStyle(screenSize, styles, 'tableHeading')}>{t('headingVehicleManager')}</th>
                      )}
                      {showChainFields && (
                        <th style={getStyle(screenSize, styles, 'tableHeading')}>{t('headingWorkshop')}</th>
                      )}
                      <th style={getStyle(screenSize, styles, 'tableHeading')}>{t('headingBusinessUnit')}</th>
                      <th style={getStyle(screenSize, styles, 'tableHeading')}>{t('headingAssetCategory')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => (
                      <OrganizationVehicleSettingsRow
                        key={row.vehicle_id}
                        row={row}
                        screenSize={screenSize}
                        rowStatus={rowStatus[row.vehicle_id]}
                        canEdit={canEdit}
                        canChangeGarage={canChangeGarage}
                        garageOptions={options.garageOptions}
                        showChainFields={showChainFields}
                        vehicleManagerOptions={options.vehicleManagerOptions}
                        workshopOptions={options.workshopOptions}
                        businessUnitOptions={options.businessUnitOptions}
                        assetCategoryOptions={options.assetCategoryOptions}
                        onFieldChange={this.onFieldChange}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {filteredRows.length > visibleCount && (
              <button
                type='button'
                style={getStyle(screenSize, styles, 'showMore')}
                onClick={() => this.setState({ visibleCount: visibleCount + SHOW_MORE_STEP })}
              >
                {t('showMore', { ns: 'common' })}
              </button>
            )}
          </div>
        )}
      </CBMediaQuery>
    );
  }
}

const styles = {
  default: {
    container: {
      padding: 32,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    },
    embeddedContainer: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    },
    sectionContainer: {
      marginBottom: 16,
    },
    hint: {
      color: '#ffe0b2',
      fontSize: 14,
      marginTop: 8,
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      marginBottom: 16,
      flexWrap: 'wrap',
    },
    searchInput: {
      minWidth: 240,
      height: 36,
      border: '1px solid rgb(255 255 255 / 55%)',
      borderRadius: 50,
      backgroundColor: 'transparent',
      color: '#FFF',
      padding: '0 16px',
      fontSize: 14,
    },
    count: {
      color: 'rgba(255, 255, 255, 0.8)',
      fontSize: 13,
    },
    tableHeading: {
      fontFamily: 'TitilliumWeb-Regular',
      fontSize: 12,
      color: '#ffffff',
      paddingLeft: 8,
      paddingBottom: 8,
      textAlign: 'left',
    },
    showMore: {
      alignSelf: 'flex-start',
      marginTop: 12,
      background: 'none',
      border: '1px solid rgba(255, 255, 255, 0.55)',
      borderRadius: 50,
      color: '#fff',
      padding: '6px 16px',
      cursor: 'pointer',
    },
  },
  medium: {
    container: {
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    },
  },
};

function mapStateToProps(state) {
  return {
    user: state.authUser.user,
    appService: state.app.service,
    selectedOrganizationId: state.organization.selectedOrganization,
    rows: state.organizationVehicleSettings.organizationVehicleSettings,
    choices: state.organizationVehicleSettings.organizationVehicleSettingsChoices,
    status: state.organizationVehicleSettings.organizationVehicleSettingsStatus,
    rowStatus: state.organizationVehicleSettings.organizationVehicleSettingsRowStatus,
    businessUnits: state.businessUnit.businessUnits,
    assetCategories: state.assetCategory.assetCategories,
  };
}

export default connect(
  mapStateToProps,
  {
    fetchOrganizationVehicleSettings: _fetchOrganizationVehicleSettings,
    fetchOrganizationVehicleSettingsChoices: _fetchOrganizationVehicleSettingsChoices,
    updateOrganizationVehicleSettings: _updateOrganizationVehicleSettings,
    fetchBusinessUnits: _fetchBusinessUnits,
    fetchAssetCategories: _fetchAssetCategories,
  },
)(translate('VehicleSettings')(SettingsOrganizationVehicleSettings));
